import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

import { api, ApiError } from "../api/client";
import type { Diet, Localized } from "../api/types";
import type { RawCategory, RawGroup, RawItem, RawOption } from "../data/menu";
import { useLanguage } from "../i18n/LanguageContext";
import { imageUrl } from "../lib/images";
import { LANGUAGES, type Language, type StringKey } from "../i18n/strings";
import "./MenuEditor.css";

/**
 * The menu editor — the whole card as one draft, saved in one go.
 *
 * Every text exists in four languages; the language tabs at the top decide
 * which one the inputs show, so the form stays one column instead of four.
 */

const DAYPARTS: RawCategory["daypart"][] = ["morning", "afternoon", "evening", "all"];
const DAYPART_LABEL: Record<RawCategory["daypart"], StringKey> = {
  morning: "daypartMorning",
  afternoon: "daypartAfternoon",
  evening: "daypartEvening",
  all: "daypartAll",
};

const emptyLocalized = (): Localized => ({ de: "", en: "", fr: "", es: "" });

function nextId(all: { id: number }[]): number {
  return all.reduce((m, x) => Math.max(m, x.id), 0) + 1;
}

function slugify(text: string, id: number): string {
  const base = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return base ? `${base}-${id}` : `x-${id}`;
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export default function MenuEditor() {
  const { t, language } = useLanguage();
  const [saved, setSaved] = useState<RawCategory[] | null>(null);
  const [draft, setDraft] = useState<RawCategory[] | null>(null);
  const [lang, setLang] = useState<Language>(language);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const menu = await api.staff.menu();
      setSaved(menu);
      setDraft(structuredClone(menu));
    } catch {
      setError(t("menuLoadError"));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);

  // Leaving with unsaved work asks first.
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const menu = await api.staff.saveMenu(draft);
      setSaved(menu);
      setDraft(structuredClone(menu));
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? `${t("somethingWrong")} (${err.message})` : t("somethingWrong"));
    } finally {
      setSaving(false);
    }
  };

  const update = (fn: (draft: RawCategory[]) => RawCategory[]) => setDraft((d) => (d ? fn(d) : d));

  const updateCategory = (ci: number, patch: Partial<RawCategory>) =>
    update((d) => d.map((c, i) => (i === ci ? { ...c, ...patch } : c)));

  const updateItem = (ci: number, ii: number, patch: Partial<RawItem>) =>
    update((d) => d.map((c, i) => (i === ci ? { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : c)));

  const addCategory = () =>
    update((d) => {
      const id = nextId(d);
      const name = emptyLocalized();
      name[lang] = t("newCategoryName");
      return [...d, { id, slug: slugify(t("newCategoryName"), id), name, description: emptyLocalized(), daypart: "all", items: [] }];
    });

  const addItem = (ci: number) =>
    update((d) => {
      const allItems = d.flatMap((c) => c.items);
      const id = nextId(allItems);
      const name = emptyLocalized();
      name[lang] = t("newItemName");
      const item: RawItem = {
        id,
        slug: slugify(t("newItemName"), id),
        name,
        description: emptyLocalized(),
        price: "0.00",
        image: null,
        diet: null,
        is_seasonal: false,
        is_favourite: false,
        allergens: emptyLocalized(),
        is_available: true,
        modifier_groups: [],
      };
      setOpen(id);
      return d.map((c, i) => (i === ci ? { ...c, items: [...c.items, item] } : c));
    });

  if (error && !draft) {
    return (
      <main className="dash-main">
        <div className="dash-empty">
          <p>{error}</p>
          <button className="btn btn-outline btn-sm" onClick={load}>{t("retry")}</button>
        </div>
      </main>
    );
  }

  if (!draft) return <main className="dash-main"><div className="skeleton me-skeleton" /></main>;

  return (
    <main className="dash-main me">
      <div className="dash-head">
        <div className="dash-bar-title">
          <strong>{t("menuEditorTitle")}</strong>
          <span className="tiny">{t("menuEditorHint")}</span>
        </div>
        <div className="me-langs" role="group" aria-label={t("languageTab")}>
          {LANGUAGES.map((code) => (
            <button key={code} className={`chip${lang === code ? " is-active" : ""}`} onClick={() => setLang(code)} lang={code}>
              {code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="dash-hint">{error}</p>}

      <div className="me-categories">
        {draft.map((category, ci) => (
          <section key={category.id} className="me-category">
            <header className="me-category-head">
              <div className="me-category-fields">
                <LocalizedInput
                  className="me-category-name"
                  value={category.name}
                  lang={lang}
                  placeholder={t("categoryName")}
                  onChange={(name) => updateCategory(ci, { name, slug: slugify(name.en || name.de, category.id) })}
                />
                <LocalizedInput
                  value={category.description}
                  lang={lang}
                  placeholder={t("categoryDesc")}
                  onChange={(description) => updateCategory(ci, { description })}
                />
                <select
                  className="input me-select"
                  value={category.daypart}
                  onChange={(e) => updateCategory(ci, { daypart: e.target.value as RawCategory["daypart"] })}
                  aria-label={t("daypart")}
                >
                  {DAYPARTS.map((d) => (
                    <option key={d} value={d}>{t(DAYPART_LABEL[d])}</option>
                  ))}
                </select>
              </div>
              <div className="me-row-actions">
                <IconButton label={t("moveUp")} onClick={() => update((d) => move(d, ci, ci - 1))} disabled={ci === 0}>↑</IconButton>
                <IconButton label={t("moveDown")} onClick={() => update((d) => move(d, ci, ci + 1))} disabled={ci === draft.length - 1}>↓</IconButton>
                <IconButton
                  label={t("deleteCategory")}
                  danger
                  onClick={() => {
                    if (window.confirm(t("confirmDeleteCategory"))) update((d) => d.filter((_, i) => i !== ci));
                  }}
                >
                  ✕
                </IconButton>
              </div>
            </header>

            <ul className="me-items">
              {category.items.map((item, ii) => {
                const expanded = open === item.id;
                return (
                  <li key={item.id} className={`me-item${item.is_available === false ? " is-off" : ""}`}>
                    <div className="me-item-row">
                      <LocalizedInput
                        className="me-item-name"
                        value={item.name}
                        lang={lang}
                        placeholder={t("itemName")}
                        onChange={(name) => updateItem(ci, ii, { name, slug: slugify(name.en || name.de, item.id) })}
                      />
                      <label className="me-price">
                        <span className="visually-hidden">{t("price")}</span>
                        <input
                          className="input nums"
                          inputMode="decimal"
                          value={item.price}
                          onChange={(e) => updateItem(ci, ii, { price: e.target.value })}
                          onBlur={(e) => {
                            const n = Number(e.target.value.replace(",", "."));
                            if (Number.isFinite(n)) updateItem(ci, ii, { price: n.toFixed(2) });
                          }}
                        />
                        <span className="me-price-unit">€</span>
                      </label>
                      <div className="me-flags">
                        <Toggle on={item.is_available !== false} label={item.is_available !== false ? t("available") : t("unavailable")} onChange={(v) => updateItem(ci, ii, { is_available: v })} />
                        <Toggle on={Boolean(item.is_favourite)} label={t("favourite")} onChange={(v) => updateItem(ci, ii, { is_favourite: v })} />
                        <Toggle on={Boolean(item.is_seasonal)} label={t("seasonal")} onChange={(v) => updateItem(ci, ii, { is_seasonal: v })} />
                      </div>
                      <div className="me-row-actions">
                        <IconButton label={t("editItem")} active={expanded} onClick={() => setOpen(expanded ? null : item.id)}>✎</IconButton>
                        <IconButton label={t("moveUp")} disabled={ii === 0} onClick={() => updateCategory(ci, { items: move(category.items, ii, ii - 1) })}>↑</IconButton>
                        <IconButton label={t("moveDown")} disabled={ii === category.items.length - 1} onClick={() => updateCategory(ci, { items: move(category.items, ii, ii + 1) })}>↓</IconButton>
                        <IconButton
                          label={t("deleteItem")}
                          danger
                          onClick={() => {
                            if (window.confirm(t("confirmDelete"))) updateCategory(ci, { items: category.items.filter((_, j) => j !== ii) });
                          }}
                        >
                          ✕
                        </IconButton>
                      </div>
                    </div>

                    <AnimatePresence initial={false}>
                      {expanded && (
                        <motion.div
                          className="me-item-detail"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        >
                          <div className="me-detail-grid">
                            <div className="field me-span">
                              <label className="label">{t("itemDesc")}</label>
                              <LocalizedInput multiline value={item.description} lang={lang} onChange={(description) => updateItem(ci, ii, { description })} />
                            </div>
                            <div className="field">
                              <label className="label">{t("allergens")}</label>
                              <LocalizedInput value={item.allergens} lang={lang} onChange={(allergens) => updateItem(ci, ii, { allergens })} />
                            </div>
                            <div className="field">
                              <label className="label">{t("diet")}</label>
                              <select className="input" value={item.diet ?? ""} onChange={(e) => updateItem(ci, ii, { diet: (e.target.value || null) as Diet | null })}>
                                <option value="">{t("dietNone")}</option>
                                <option value="vegetarian">{t("vegetarian")}</option>
                                <option value="vegan">{t("vegan")}</option>
                              </select>
                            </div>
                            <div className="field me-span">
                              <label className="label">{t("imageUrl")}</label>
                              <div className="me-image-row">
                                <input className="input" value={item.image ?? ""} placeholder="https://…" onChange={(e) => updateItem(ci, ii, { image: e.target.value || null })} />
                                <UploadButton onUploaded={(url) => updateItem(ci, ii, { image: url })} />
                                {item.image && <img className="me-thumb" src={imageUrl(item.image, 120)} alt="" />}
                              </div>
                            </div>
                          </div>

                          <GroupsEditor
                            groups={item.modifier_groups ?? []}
                            lang={lang}
                            onChange={(modifier_groups) => updateItem(ci, ii, { modifier_groups })}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                );
              })}
            </ul>

            <button className="btn btn-ghost btn-sm me-add" onClick={() => addItem(ci)}>
              + {t("addItem")}
            </button>
          </section>
        ))}

        <button className="btn btn-outline me-add-category" onClick={addCategory}>
          + {t("addCategory")}
        </button>
      </div>

      <AnimatePresence>
        {(dirty || flash) && (
          <motion.div
            className="me-savebar"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 34 }}
          >
            <span className="me-savebar-text">{flash && !dirty ? `✓ ${t("savedOk")}` : t("unsavedChanges")}</span>
            {dirty && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={() => setDraft(structuredClone(saved))} disabled={saving}>
                  {t("discard")}
                </button>
                <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                  {saving ? t("sending") : t("saveChanges")}
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

// --- modifier groups ---------------------------------------------------------

function GroupsEditor({ groups, lang, onChange }: { groups: RawGroup[]; lang: Language; onChange: (groups: RawGroup[]) => void }) {
  const { t } = useLanguage();

  const updateGroup = (gi: number, patch: Partial<RawGroup>) => onChange(groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));
  const updateOption = (gi: number, oi: number, patch: Partial<RawOption>) =>
    updateGroup(gi, { options: groups[gi].options.map((o, j) => (j === oi ? { ...o, ...patch } : o)) });

  const addGroup = () =>
    onChange([
      ...groups,
      { id: nextId(groups), name: emptyLocalized(), selection_type: "single", min_select: 1, max_select: 1, is_required: true, options: [] },
    ]);

  const addOption = (gi: number) =>
    updateGroup(gi, {
      options: [...groups[gi].options, { id: nextId(groups.flatMap((g) => g.options)), name: emptyLocalized(), price: "0.00", is_default: false, is_removable: true }],
    });

  return (
    <div className="me-groups">
      <div className="me-groups-head">
        <span className="label">{t("optionGroups")}</span>
        <button className="btn btn-ghost btn-sm" onClick={addGroup}>+ {t("addGroup")}</button>
      </div>

      {groups.map((group, gi) => (
        <div key={group.id} className="me-group">
          <div className="me-group-row">
            <LocalizedInput className="grow" value={group.name} lang={lang} placeholder={t("optionGroups")} onChange={(name) => updateGroup(gi, { name })} />
            <select
              className="input me-select"
              value={group.selection_type}
              onChange={(e) => {
                const multi = e.target.value === "multi";
                updateGroup(gi, { selection_type: multi ? "multi" : "single", min_select: multi ? 0 : 1, max_select: multi ? 0 : 1, is_required: !multi });
              }}
            >
              <option value="single">{t("groupSingle")}</option>
              <option value="multi">{t("groupMulti")}</option>
            </select>
            <IconButton label={t("actDelete")} danger onClick={() => onChange(groups.filter((_, i) => i !== gi))}>✕</IconButton>
          </div>

          <ul className="me-options">
            {group.options.map((option, oi) => (
              <li key={option.id} className="me-option">
                <LocalizedInput className="grow" value={option.name} lang={lang} placeholder={t("addOption")} onChange={(name) => updateOption(gi, oi, { name })} />
                <label className="me-price">
                  <span className="visually-hidden">{t("extraPrice")}</span>
                  <input className="input nums" inputMode="decimal" value={option.price} onChange={(e) => updateOption(gi, oi, { price: e.target.value })} />
                  <span className="me-price-unit">€</span>
                </label>
                <Toggle
                  on={option.is_default}
                  label={t("optionDefault")}
                  onChange={(v) => {
                    // A single-choice group has one default at most.
                    if (group.selection_type === "single" && v) {
                      updateGroup(gi, { options: group.options.map((o, j) => ({ ...o, is_default: j === oi })) });
                    } else updateOption(gi, oi, { is_default: v });
                  }}
                />
                <IconButton label={t("actDelete")} danger onClick={() => updateGroup(gi, { options: group.options.filter((_, j) => j !== oi) })}>✕</IconButton>
              </li>
            ))}
          </ul>
          <button className="btn btn-ghost btn-sm" onClick={() => addOption(gi)}>+ {t("addOption")}</button>
        </div>
      ))}
    </div>
  );
}

// --- small parts -------------------------------------------------------------

/** A file picker that sends the photo to Cloudinary and hands back its URL. */
function UploadButton({ onUploaded }: { onUploaded: (url: string) => void }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <label className={`btn btn-outline btn-sm me-upload${busy ? " is-busy" : ""}`}>
      <input
        type="file"
        accept="image/*"
        className="visually-hidden"
        disabled={busy}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          setBusy(true);
          setFailed(false);
          try {
            const { url } = await api.staff.upload(file);
            onUploaded(url);
          } catch {
            setFailed(true);
          } finally {
            setBusy(false);
          }
        }}
      />
      {busy ? t("sending") : failed ? t("uploadFailed") : t("uploadPhoto")}
    </label>
  );
}

function LocalizedInput({
  value,
  lang,
  onChange,
  placeholder,
  className,
  multiline,
}: {
  value: Localized;
  lang: Language;
  onChange: (next: Localized) => void;
  placeholder?: string;
  className?: string;
  multiline?: boolean;
}) {
  // The German text shows through as a hint when this language is still empty.
  const hint = value[lang] ? "" : value.de || placeholder || "";
  const props = {
    className: `input${className ? ` ${className}` : ""}`,
    value: value[lang],
    placeholder: hint,
    lang,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...value, [lang]: e.target.value }),
  };
  return multiline ? <textarea {...props} rows={2} /> : <input {...props} />;
}

function Toggle({ on, label, onChange }: { on: boolean; label: string; onChange: (v: boolean) => void }) {
  return (
    <button type="button" className={`me-toggle${on ? " is-on" : ""}`} onClick={() => onChange(!on)} aria-pressed={on}>
      <span className="me-toggle-dot" aria-hidden="true" />
      {label}
    </button>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  danger,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`me-icon${danger ? " is-danger" : ""}${active ? " is-active" : ""}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}
